import React from 'react';
import { motion } from 'framer-motion';

const benefits = [
  {
    title: 'Proyectos Tecnológicos',
    description: 'Participa en desarrollo de soluciones reales con impacto regional y global.',
    icon: (
      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: 'Workshops y Hackathons',
    description: 'Accede a actividades intensivas de aprendizaje en IA, robótica y comunicaciones.',
    icon: (
      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="16" rx="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 8h8M8 12h8" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: 'Liderazgo Estudiantil',
    description: 'Desarrolla gestión de equipos, organización de eventos y toma de decisiones estratégicas.',
    icon: (
      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 3a4 4 0 0 1 4 4v2H8V7a4 4 0 0 1 4-4z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6 20v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: 'Red Internacional',
    description: 'Conecta con ingenieros, investigadores y empresas de todo el mundo.',
    icon: (
      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2a10 10 0 0 1 7.07 17.07A10 10 0 1 1 4.93 4.93 9.96 9.96 0 0 1 12 2z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2 12h20M12 2a10 10 0 0 1 0 20" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: 'Acceso a Recursos IEEE',
    description: 'Obtén acceso a papers, bases de datos, certificaciones y herramientas educativas.',
    icon: (
      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 5h16v14H4z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 7h8M8 12h8" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: 'Impacto Social',
    description: 'Aplica la tecnología para resolver problemas reales de la comunidad y la región.',
    icon: (
      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 20l-7-7a7 7 0 0 1 10-10l1 1 1-1a7 7 0 0 1 10 10z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

const StudentBranch: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 },
    },
  };

  return (
    <section className="relative overflow-hidden bg-white py-24 sm:py-32 lg:py-40" id="student-branch">
      {/* Ambient background optimized for light mode */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 left-1/4 w-[500px] h-[500px] rounded-full blur-[140px] opacity-[0.05] bg-gradient-to-r from-indigo-400/30 to-transparent" />
        <div className="absolute bottom-1/3 right-1/4 w-[600px] h-[600px] rounded-full blur-[130px] opacity-[0.04] bg-gradient-to-l from-cyan-400/20 to-transparent" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 sm:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="space-y-16"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="space-y-10 max-w-3xl">
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-lg bg-slate-50 border border-slate-200 shadow-sm">
              <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.4)]" />
              <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                Rama Estudiantil
              </span>
            </div>

            <div className="space-y-6">
              <h2 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.12] tracking-tight text-slate-900">
                Qué significa formar parte de{' '}
                <span className="text-indigo-600">
                  IEEE ESPOCH
                </span>
              </h2>
              <p className="text-lg sm:text-xl text-slate-600 leading-relaxed font-normal max-w-3xl">
                Una rama estudiantil es la comunidad oficial de IEEE dentro de la universidad, donde el talento técnico se organiza en proyectos y redes estratégicas de alto impacto.
              </p>
            </div>
          </motion.div>

          {/* Benefits grid */}
          <motion.div
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -6 }}
                className="group relative rounded-3xl p-8 lg:p-10 border border-slate-100 bg-white transition-all duration-500 hover:border-indigo-200 hover:shadow-premium"
              >
                <div className="relative space-y-6">
                  {/* Icon container */}
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-slate-50 text-indigo-600 border border-slate-100 transition-all duration-500 group-hover:bg-indigo-50 group-hover:border-indigo-100">
                    {benefit.icon}
                  </div>

                  {/* Content */}
                  <div className="space-y-3">
                    <h3 className="text-xl lg:text-2xl font-bold text-slate-900 leading-snug">
                      {benefit.title}
                    </h3>
                    <p className="text-base text-slate-500 leading-relaxed font-normal">
                      {benefit.description}
                    </p>
                  </div>
                </div>

                {/* Accent line */}
                <div className="absolute bottom-0 left-8 right-8 h-1 bg-gradient-to-r from-indigo-500/40 to-transparent w-0 group-hover:w-[calc(100%-64px)] transition-all duration-500 rounded-full" />
              </motion.div>
            ))}
          </motion.div>

          {/* Showcase section */}
          <motion.div
            variants={itemVariants}
            className="rounded-[2.5rem] border border-slate-100 bg-slate-50/50 p-12 lg:p-16 space-y-12"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Left content */}
              <div className="space-y-8 flex flex-col justify-center">
                <div className="space-y-4">
                  <h3 className="text-3xl lg:text-4xl font-black text-slate-900 leading-tight">
                    Un espacio para el talento tecnológico
                  </h3>
                  <p className="text-lg text-slate-600 leading-relaxed font-normal">
                    IEEE ESPOCH integra a estudiantes de ingeniería, investigación y diseño para trabajar en iniciativas de alto valor. Somos el punto de encuentro entre la academia, la industria y la comunidad global de innovación.
                  </p>
                </div>

                <motion.a
                  href="#chapters"
                  whileHover={{ scale: 1.02, x: 6 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-3 w-fit px-8 py-3 rounded-xl font-bold text-sm uppercase tracking-[0.2em] text-indigo-600 bg-white border border-indigo-100 hover:bg-indigo-50 transition-all duration-300 shadow-sm"
                >
                  Explorar iniciativas
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </motion.a>
              </div>

              {/* Right content - organized list */}
              <div className="space-y-8">
                <h4 className="text-2xl lg:text-3xl font-black text-slate-900">
                  Organizamos
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    'Workshops técnicos especializados',
                    'Hackathons con retos reales',
                    'Conferencias y jornadas académicas',
                    'Redes de networking profesional',
                  ].map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -16 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: idx * 0.1 }}
                      viewport={{ once: true }}
                      className="flex items-center gap-3 text-slate-600 group"
                    >
                      <div className="w-2 h-2 rounded-full bg-indigo-500/40 flex-shrink-0 group-hover:bg-indigo-500 transition-colors duration-300" />
                      <span className="text-base leading-relaxed font-medium">
                        {item}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default StudentBranch;
